import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CodigoTributacaoNacionalCombobox from "./CodigoTributacaoNacionalCombobox";
import MunicipioCombobox from "./MunicipioCombobox";
import { toast } from "sonner";
import { ShieldCheck, Upload, Trash2, Wifi, AlertTriangle, CheckCircle2, Lock } from "lucide-react";

// NFS-e Nacional — Fase 1 (preparação/Produção Restrita). Ver
// docs/NFSE_NACIONAL.md — nenhuma ação nesta tela emite nota com validade
// jurídica; "Testar configuração fiscal" NUNCA emite, só confirma
// certificado + conectividade mTLS + parâmetros mínimos.
const CERT_STATUS_LABEL = {
  valido: "Válido", vence_30_dias: "Vence em até 30 dias", vence_15_dias: "Vence em até 15 dias",
  vence_7_dias: "Vence em até 7 dias", expirado: "Expirado", desconhecido: "Desconhecido",
};
const CERT_STATUS_CLASS = {
  valido: "bg-emerald-100 text-emerald-800", vence_30_dias: "bg-amber-100 text-amber-800",
  vence_15_dias: "bg-amber-100 text-amber-800", vence_7_dias: "bg-red-100 text-red-800",
  expirado: "bg-red-100 text-red-800", desconhecido: "bg-slate-100 text-slate-600",
};

// Ambiente EFETIVAMENTE testado (delta 2026-09-19, item 8 do pedido) —
// nunca hardcoded como "Produção Restrita": usa `ambiente_label` do
// backend (fonte da verdade) e só cai para o cálculo local se um
// snapshot antigo, salvo antes desta correção, ainda não tiver o campo.
function ambienteLabel(testResult) {
  return testResult?.ambiente_label || (testResult?.ambiente === "producao" ? "Produção" : "Produção Restrita");
}

// Uma linha de diagnóstico do "Testar configuração fiscal" (delta
// 2026-09-18 — verificação pré-produção: A/B/C/D separados, nunca um
// resultado único). `ok` é tri-state: true (verde), false (âmbar/erro),
// null/undefined (cinza — indeterminado, nunca apresentado como sucesso
// nem como falha).
function DiagnosticoLine({ testId, ok, label, detail }) {
  const cls = ok === true ? "bg-emerald-50 text-emerald-800" : ok === false ? "bg-amber-50 text-amber-800" : "bg-slate-50 text-slate-600";
  return (
    <div className={`rounded-md p-3 text-sm ${cls}`} data-testid={testId}>
      <div className="flex items-center gap-1 font-medium">
        {ok === true ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
        {label}
      </div>
      {detail && <div className="text-xs mt-1">{detail}</div>}
    </div>
  );
}

const FORM_DEFAULTS = {
  active: false, ambiente: "producao_restrita", cnpj: "", cpf: "", inscricao_municipal: "",
  codigo_municipio_ibge: "", opcao_simples_nacional: "nao_optante", regime_especial_tributacao: "0",
  codigo_servico_nacional: "", codigo_tributacao_municipal: "", codigo_nbs: "",
  tributacao_issqn: "tributavel", retencao_issqn: "nao_retido", aliquota_issqn: "", serie_dps: "00001",
};

export default function FiscalNfseConfig() {
  const [form, setForm] = useState(FORM_DEFAULTS);
  const [certificado, setCertificado] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pfxFile, setPfxFile] = useState(null);
  const [pfxSenha, setPfxSenha] = useState("");
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [codigoServicoInfo, setCodigoServicoInfo] = useState(null);
  const [municipioInfo, setMunicipioInfo] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/config/fiscal/nfse");
      setForm({ ...FORM_DEFAULTS, ...data, aliquota_issqn: data.aliquota_issqn ?? "" });
      setCertificado(data.certificado);
      setTestResult(data.last_test_result || null);
      setCodigoServicoInfo(data.codigo_servico_nacional_info || null);
      setMunicipioInfo(data.codigo_municipio_ibge_info || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, aliquota_issqn: form.aliquota_issqn === "" ? null : Number(form.aliquota_issqn) };
      await api.put("/config/fiscal/nfse", body);
      toast.success("Configuração fiscal salva");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadCertificado = async () => {
    if (!pfxFile) return toast.error("Selecione o arquivo .pfx/.p12");
    if (!pfxSenha) return toast.error("Informe a senha do certificado");
    setUploading(true);
    try {
      const pfx_data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(pfxFile);
      });
      await api.post("/config/fiscal/nfse/certificado", { pfx_data, senha: pfxSenha });
      toast.success("Certificado validado e salvo com segurança");
      setPfxFile(null); setPfxSenha("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const removerCertificado = async () => {
    try {
      await api.delete("/config/fiscal/nfse/certificado");
      toast.success("Certificado removido");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const testar = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/config/fiscal/nfse/testar");
      setTestResult(data);
      // `data.ok` reflete SOMENTE certificado + comunicação/mTLS (nunca
      // o município — verificação 2026-09-19: antes, um município "não
      // parametrizado" podia virar `ok=false` e o toast acusava falsamente
      // "falha na comunicação/autenticação" mesmo com mTLS 100% OK). É
      // tri-state: true/false/null (null = indeterminado, ex.: 404 na
      // consulta de distribuição em Produção Restrita).
      if (data.ok === false) {
        toast.warning("Falha na comunicação/autenticação com o ADN — veja os detalhes abaixo");
      } else if (data.ok === true) {
        // mTLS/certificado OK — mas só é sucesso PLENO se o município (e,
        // quando executada, a consulta de distribuição) também vierem OK;
        // caso contrário é uma pendência de outro diagnóstico, nunca uma
        // falha de autenticação.
        const municipioOk = data.municipio?.ok;
        const dfeOk = data.consulta_dfe ? data.consulta_dfe.ok : true;
        if (municipioOk === true && dfeOk !== false) toast.success("Certificado e mTLS confirmados pelo ADN");
        else toast.info("Teste concluído com pendências — veja os detalhes abaixo");
      } else {
        toast.info("Teste concluído — veja os detalhes abaixo (resultado indeterminado)");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="p-4 bg-blue-50 border-blue-200 text-xs text-blue-900">
        <b>Fase 1 — preparação.</b> Toda comunicação real aponta somente para a <b>Produção Restrita</b> da
        NFS-e Nacional (ambiente de testes oficial). Emissão com validade jurídica ainda não está disponível
        — ver <code>docs/NFSE_NACIONAL.md</code>.
      </Card>

      <Card className="p-5 space-y-4" data-testid="fiscal-nfse-config">
        <div className="flex items-center justify-between">
          <div className="font-heading font-semibold">Dados fiscais do prestador</div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} data-testid="nfse-active" />
            NFS-e habilitada para esta empresa
          </label>
        </div>

        <div>
          <Label className="text-xs">Ambiente</Label>
          <Select value={form.ambiente} onValueChange={v => set("ambiente", v)}>
            <SelectTrigger data-testid="nfse-ambiente"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="producao_restrita">Produção Restrita (testes)</SelectItem>
              <SelectItem value="producao">Produção</SelectItem>
            </SelectContent>
          </Select>
          {form.ambiente === "producao" && (
            <p className="text-xs text-amber-700 mt-1">
              O backend ainda BLOQUEIA chamadas reais de Produção nesta fase, mesmo com esta opção selecionada
              (feature gate do servidor) — use Produção Restrita.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">CNPJ do prestador</Label>
            <Input value={form.cnpj || ""} onChange={e => set("cnpj", e.target.value)} placeholder="00.000.000/0000-00" data-testid="nfse-cnpj" />
          </div>
          <div>
            <Label className="text-xs">CPF (se autônomo, em vez de CNPJ)</Label>
            <Input value={form.cpf || ""} onChange={e => set("cpf", e.target.value)} placeholder="000.000.000-00" data-testid="nfse-cpf" />
          </div>
          <div>
            <Label className="text-xs">Inscrição municipal</Label>
            <Input value={form.inscricao_municipal || ""} onChange={e => set("inscricao_municipal", e.target.value)} data-testid="nfse-im" />
          </div>
          <div>
            <Label className="text-xs">Município</Label>
            <MunicipioCombobox
              value={form.codigo_municipio_ibge || ""}
              info={municipioInfo}
              onChange={(m) => {
                set("codigo_municipio_ibge", m.codigo_ibge);
                setMunicipioInfo({ nome: m.nome, uf: m.uf, valido: true });
              }}
              testId="nfse-ibge"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Situação perante o Simples Nacional</Label>
            <Select value={form.opcao_simples_nacional} onValueChange={v => set("opcao_simples_nacional", v)}>
              <SelectTrigger data-testid="nfse-simples"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nao_optante">Não optante</SelectItem>
                <SelectItem value="mei">Optante — MEI</SelectItem>
                <SelectItem value="me_epp">Optante — ME/EPP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Regime especial de tributação</Label>
            <Select value={form.regime_especial_tributacao || "0"} onValueChange={v => set("regime_especial_tributacao", v)}>
              <SelectTrigger data-testid="nfse-regime-especial"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Nenhum</SelectItem>
                <SelectItem value="1">Ato Cooperado (Cooperativa)</SelectItem>
                <SelectItem value="2">Estimativa</SelectItem>
                <SelectItem value="3">Microempresa Municipal</SelectItem>
                <SelectItem value="4">Notário ou Registrador</SelectItem>
                <SelectItem value="5">Profissional Autônomo</SelectItem>
                <SelectItem value="6">Sociedade de Profissionais</SelectItem>
                <SelectItem value="9">Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Código de Tributação Nacional</Label>
            <CodigoTributacaoNacionalCombobox
              value={form.codigo_servico_nacional || ""}
              info={codigoServicoInfo}
              onChange={(item) => {
                set("codigo_servico_nacional", item.codigo);
                setCodigoServicoInfo({ codigo_exibicao: item.codigo_exibicao, descricao: item.descricao, valido: true });
              }}
            />
            <p className="text-xs text-slate-400 mt-1">
              Selecione o serviço conforme a Lista de Serviços Nacional da NFS-e.
            </p>
          </div>
          <div>
            <Label className="text-xs">Código NBS (opcional)</Label>
            <Input value={form.codigo_nbs || ""} onChange={e => set("codigo_nbs", e.target.value)} data-testid="nfse-nbs" />
          </div>
          <div>
            <Label className="text-xs">Tributação do ISSQN</Label>
            <Select value={form.tributacao_issqn} onValueChange={v => set("tributacao_issqn", v)}>
              <SelectTrigger data-testid="nfse-tributacao-issqn"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tributavel">Operação tributável</SelectItem>
                <SelectItem value="imunidade">Imunidade</SelectItem>
                <SelectItem value="exportacao">Exportação de serviço</SelectItem>
                <SelectItem value="nao_incidencia">Não incidência</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Retenção do ISSQN</Label>
            <Select value={form.retencao_issqn} onValueChange={v => set("retencao_issqn", v)}>
              <SelectTrigger data-testid="nfse-retencao-issqn"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nao_retido">Não retido</SelectItem>
                <SelectItem value="retido_tomador">Retido pelo tomador</SelectItem>
                <SelectItem value="retido_intermediario">Retido pelo intermediário</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Alíquota do ISSQN (%) — só se o município não pertencer ao Sistema Nacional</Label>
            <Input type="number" step="0.01" value={form.aliquota_issqn} onChange={e => set("aliquota_issqn", e.target.value)} data-testid="nfse-aliquota" />
          </div>
          <div>
            <Label className="text-xs">Série da DPS</Label>
            <Input value={form.serie_dps || ""} onChange={e => set("serie_dps", e.target.value)} data-testid="nfse-serie" />
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="nfse-salvar">{saving ? "Salvando..." : "Salvar dados fiscais"}</Button>
      </Card>

      <Card className="p-5 space-y-4" data-testid="fiscal-nfse-certificado">
        <div className="font-heading font-semibold flex items-center gap-2"><ShieldCheck size={16} /> Certificado Digital A1</div>

        {certificado ? (
          <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
            <div className="flex items-center justify-between">
              <b>{certificado.titular}</b>
              <Badge className={CERT_STATUS_CLASS[certificado.status]}>{CERT_STATUS_LABEL[certificado.status]}</Badge>
            </div>
            <div className="text-xs text-slate-500">Emissor: {certificado.emissor}</div>
            <div className="text-xs text-slate-500">Documento: {certificado.cnpj_cpf_mascarado || "não identificável no certificado"}</div>
            <div className="text-xs text-slate-500">
              Validade: {certificado.not_before?.slice(0, 10)} a {certificado.not_after?.slice(0, 10)}
              {certificado.dias_para_vencer != null && certificado.dias_para_vencer >= 0 && ` (${certificado.dias_para_vencer} dia(s) restantes)`}
            </div>
            {certificado.compatible_with_tenant_document === false && (
              <div className="text-xs text-red-700 flex items-center gap-1 mt-1"><AlertTriangle size={12} /> O CNPJ/CPF do certificado não confere com o cadastrado acima.</div>
            )}
            {certificado.status === "expirado" && (
              <div className="text-xs text-red-700 flex items-center gap-1 mt-1"><AlertTriangle size={12} /> Certificado expirado — emissão bloqueada. Envie um novo.</div>
            )}
            <Button size="sm" variant="outline" className="text-red-600 mt-2" onClick={removerCertificado} data-testid="nfse-cert-remover">
              <Trash2 size={14} /> Remover certificado
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Arquivo (.pfx ou .p12)</Label>
                <input type="file" accept=".pfx,.p12" onChange={e => setPfxFile(e.target.files?.[0] || null)} data-testid="nfse-cert-file"
                  className="block w-full text-sm border rounded-md px-2 py-1.5" />
              </div>
              <div>
                <Label className="text-xs">Senha do certificado</Label>
                <Input type="password" value={pfxSenha} onChange={e => setPfxSenha(e.target.value)} data-testid="nfse-cert-senha" />
              </div>
            </div>
            <Button onClick={uploadCertificado} disabled={uploading} data-testid="nfse-cert-enviar">
              <Upload size={14} /> {uploading ? "Validando e salvando..." : "Salvar certificado A1"}
            </Button>
            <p className="text-xs text-slate-400">
              O certificado é validado (PKCS#12 real, senha, chave privada, validade) e salvo criptografado — nunca em texto puro,
              nunca devolvido depois de salvo.
            </p>

            <div className="pt-3 border-t">
              <Button size="sm" variant="outline" disabled title="Disponível numa versão futura (LOOTUS Agent para Windows) — o navegador não acessa o repositório de certificados do Windows diretamente." data-testid="nfse-cert-opcao-windows" className="mt-2">
                <Lock size={14} /> Usar certificado instalado neste computador — Em breve
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-heading font-semibold flex items-center gap-2"><Wifi size={16} /> Testar configuração fiscal</div>
        <p className="text-xs text-slate-500">
          Testa exatamente o Ambiente selecionado acima (Produção Restrita ou Produção) — sempre com consultas
          GET somente-leitura de certificado, comunicação mTLS e parametrização/convênio do município.
          <b> Nunca emite nota, nunca envia DPS, nunca cancela.</b>
        </p>
        <Button onClick={testar} disabled={testing} data-testid="nfse-testar">{testing ? "Testando..." : "Testar configuração fiscal"}</Button>
        {testResult && (
          <div className="space-y-2" data-testid="nfse-test-resultado">
            <DiagnosticoLine
              testId="nfse-test-certificado"
              ok={testResult.certificado?.ok}
              label={`Certificado A1 — ${ambienteLabel(testResult)}`}
              detail={testResult.certificado?.ok ? null : "Certificado inválido ou expirado."}
            />
            <DiagnosticoLine
              testId="nfse-test-mtls"
              ok={testResult.comunicacao_mtls?.ok}
              label={`Comunicação HTTPS/mTLS — ${ambienteLabel(testResult)} ${testResult.comunicacao_mtls?.ok ? "OK" : "falhou"}`}
              detail={testResult.comunicacao_mtls?.detail}
            />
            {testResult.consulta_dfe && (
              <DiagnosticoLine
                testId="nfse-test-dfe"
                ok={testResult.consulta_dfe.ok}
                label={`Consulta de distribuição (DFe) — ${ambienteLabel(testResult)} — ${
                  testResult.consulta_dfe.ok === true ? "documento localizado"
                    : testResult.consulta_dfe.ok === false ? "falha"
                      : "sem documento para o NSU"
                }`}
                detail={testResult.consulta_dfe.detail}
              />
            )}
            {testResult.municipio && (
              <DiagnosticoLine
                testId="nfse-test-municipio"
                ok={testResult.municipio.ok}
                label={`Município na ${ambienteLabel(testResult)} ${
                  testResult.municipio.ok === true ? "OK" : testResult.municipio.ok === false ? "não parametrizado" : "não verificado"
                }`}
                detail={testResult.municipio.detail}
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
